import React, { useRef } from 'react'
import { Route, Switch, Redirect } from 'react-router-dom'

// Utils
import { useScrollTop } from 'utils'

// Constants
import { routes, RouteProps } from 'constants/routes'

// Components
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import GenericErrorView from 'views/GenericErrorView'

// Local components
import { BillingStatusBanners } from 'modules/workspace/components'

import { PrivateWorkspaceRouteExtraProps } from 'modules/workspace/containers/PrivateWorkspaceRoute'

import {
  WorkspaceSettingsBilling,
  WorkspaceSettingsSso,
} from 'modules/workspace/pages'

// Styles
import { PanelWrapper } from './WorkspaceSettingsView.styles'

// GQL
import {
  useWorkspaceSettings,
  useRefetchWorkspaceSettingsOnRouteChange,
} from 'modules/workspace/operations'

import WorkspaceSettingsLayout from 'modules/workspace/components/WorkspaceSettingsLayout'
import { PersonalWorkspaceSettingsGeneral } from '../../pages/WorkspaceSettingsGeneral'

// Types
interface WorkspaceSettingsProps
  extends RouteProps<'WORKSPACE_SETTINGS'>,
    PrivateWorkspaceRouteExtraProps {}

const WorkspaceSettingsGeneral = React.lazy(
  () => import('../../pages/WorkspaceSettingsGeneral')
)

const SHOW_SCA_STATUS = ['PAST_DUE']

/**
 * WorkspaceSettings
 *
 * This is the base component for the Workspace settings
 * It renders
 * - General Section
 * - Billing Section
 * - SSO Section
 */
const WorkspaceSettingsView: React.FC<WorkspaceSettingsProps> = props => {
  const { workspace, workspaces } = props

  const elementToScrollTop = useRef<HTMLElement | null>(null)

  const workspaceId = workspace.identifier
  const { data, loading, updating, error, refetch } = useWorkspaceSettings(
    workspaceId
  )
  useRefetchWorkspaceSettingsOnRouteChange(refetch, workspaceId)

  useScrollTop(elementToScrollTop.current)

  if (workspace.userRole === 'GUEST') {
    return <Redirect to={routes.WORKSPACE_SHARES.create({ workspaceId })} />
  }
  const { workspace: workspaceSettings, customer, sso } = data || {}

  /**
   * Because workspace and workspaceSettings are fetched in two different queries,
   * after upgrading a legacy workspace, the user can be in a situation where both
   * are out of sync in the cache. "workspace" already says that the workspace has been upgraded
   * but the settings is missing info we are expecting for upgraded workspaces (billing for example)
   * and we need to wait for it to be updated in the cache.
   * https://github.com/sketch-hq/Cloud/issues/8658
   */
  const isUpdatingWorkspaceSettingsInCacheAfterUpgrade =
    workspaceSettings && workspace.type !== workspaceSettings.type

  /**
   * Updates should not show the page spinner unless it's an important one that changes
   * the structure of the page (legacy workspace upgrade)
   */
  if (
    loading &&
    (!updating || isUpdatingWorkspaceSettingsInCacheAfterUpgrade)
  ) {
    return <DynamicLoadingPage />
  }

  if (error || !workspaceSettings) {
    return <GenericErrorView error={error} />
  }

  const { identifier, avatar, name, userRole } = workspace
  const userCanAdminister = userRole === 'ADMIN'
  const isPersonalWorkspace = workspace.type === 'PERSONAL'
  const isFinanceRole = userRole === 'FINANCE'

  if (isPersonalWorkspace && !workspace.userIsOwner) {
    return (
      <GenericErrorView errorTitle="Only the owner of this workspace can see its settings" />
    )
  }

  const {
    billing,
    identifier: customerId,
    canUseSso,
    ssoEnabled,
    ssoShortName,
  } = workspaceSettings.customer ?? {}
  const { status, trialEnd, seats } = billing ?? {}

  const avatarSrc = avatar?.large || undefined
  const subscriptionEnd = billing?.subscriptionEnd || undefined

  const currentPlan = customer?.subscription?.currentPlan || undefined
  const nextBillingCycleDate = customer?.subscription?.renewalDate || undefined

  /**
   * We should hide the PendingSCAToken value if the workspace is normal status
   */
  const pendingScaToken =
    status && SHOW_SCA_STATUS.includes(status)
      ? customer?.subscription?.pendingScaToken || undefined
      : undefined

  const projectedCost = customer?.billingSummary?.nextBillingCycle?.totalAmount
  const credits = customer?.credits
  const paymentDetails = customer?.paymentDetails

  return (
    <WorkspaceSettingsLayout
      ref={elementToScrollTop}
      workspace={workspace}
      workspaces={workspaces}
      canUseSso={canUseSso}>
      {status && (
        <BillingStatusBanners
          workspaceId={workspaceId}
          billingStatus={status}
          cancelPeriodEnd={subscriptionEnd}
          isUserAdmin={workspace.userRole === 'ADMIN'}
        />
      )}
      <PanelWrapper>
        <Switch>
          <Route
            path={routes.WORKSPACE_SETTINGS_GENERAL.template()}
            render={() => {
              if (isPersonalWorkspace) {
                /* There's already a FF inside there soo no need to touch it */
                return <PersonalWorkspaceSettingsGeneral name={name} />
              }

              return (
                <WorkspaceSettingsGeneral
                  workspaceId={identifier}
                  name={name}
                  avatar={avatarSrc}
                  isUserAdministrator={userCanAdminister}
                  billingStatus={status!}
                  subscriptionEnd={subscriptionEnd}
                  seats={seats!}
                  isFinanceRole={isFinanceRole}
                  /* Exclusive Administrative Props */
                  customerId={customerId!}
                  // These are optional because this page is common for both
                  // members and admins. This information is only available
                  // in PaymentQL which is queryable by admins only
                  currentPlan={currentPlan}
                  nextBillingCycleDate={nextBillingCycleDate}
                  paymentDetails={paymentDetails}
                  isSsoEnabled={ssoEnabled!}
                />
              )
            }}
            exact
          />
          {!isPersonalWorkspace && (userCanAdminister || isFinanceRole) && (
            <Route
              path={routes.WORKSPACE_SETTINGS_BILLING.template()}
              render={() => (
                <WorkspaceSettingsBilling
                  onWorkspaceSettingRefresh={refetch}
                  workspaceId={identifier}
                  subscriptionStatus={status!}
                  subscriptionEnd={subscriptionEnd}
                  seats={seats!}
                  isFinanceRole={isFinanceRole}
                  /* Exclusive Administrative Props */
                  credits={credits!}
                  customerId={customerId!}
                  currentPlan={currentPlan}
                  pendingSCAToken={pendingScaToken}
                  nextBillingCycleDate={nextBillingCycleDate}
                  billingDetails={customer!.billingDetails!}
                  paymentDetails={paymentDetails}
                  projectedCost={projectedCost}
                  trialEnd={trialEnd!}
                />
              )}
              exact
            />
          )}
          {!isPersonalWorkspace && userCanAdminister && canUseSso && (
            <Route
              path={routes.WORKSPACE_SETTINGS_SSO.template()}
              render={() => (
                <WorkspaceSettingsSso
                  customerId={customerId!}
                  ssoShortName={ssoShortName || undefined}
                  isSsoEnabled={ssoEnabled!}
                  config={sso?.ssoConfig || undefined}
                />
              )}
              exact
            />
          )}
          <Redirect
            from={routes.WORKSPACE_SETTINGS.template()}
            to={routes.WORKSPACE_SETTINGS_GENERAL.create({
              workspaceId,
            })}
          />
        </Switch>
      </PanelWrapper>
    </WorkspaceSettingsLayout>
  )
}

export default WorkspaceSettingsView
